import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BusinessIcon from "@material-ui/icons/Business";
import WarningIcon from "@material-ui/icons/Warning";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
    border: "1px solid #E0E0E0",
  },
  highlighted: {
    border: "1px solid #DC2626",
    backgroundColor: "#FEF2F2",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  title: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#1a1f36",
  },
  description: {
    fontSize: "0.8rem",
    color: "#697386",
  },
  incompleteBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: "#FFFBEB",
    border: "1px solid #FCD34D",
    borderRadius: 8,
    color: "#92400E",
    fontSize: "0.8rem",
  },
  fields: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  field: {
    backgroundColor: "white",
    flex: "1 1 220px",
  },
  fieldSmall: {
    backgroundColor: "white",
    flex: "1 1 120px",
  },
  // Le conteneur est en colonne : y appliquer `flex: 1 1 220px` ferait de 220px
  // une base de HAUTEUR et étirerait le champ. Les champs pleine largeur ne
  // doivent donc porter aucune propriété flex.
  fieldFull: {
    backgroundColor: "white",
  },
  saveButton: {
    alignSelf: "flex-start",
    backgroundColor: "#5E77FF",
    color: "white",
    textTransform: "none",
    borderRadius: 8,
    padding: "8px 20px",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#4A5FD1",
    },
  },
  confirmDialog: {
    padding: theme.spacing(4),
    maxWidth: 460,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  confirmTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#1a1f36",
  },
  confirmText: {
    fontSize: "0.9rem",
    color: "#697386",
  },
  confirmActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  secondaryButton: {
    textTransform: "none",
    borderRadius: 8,
    color: "#697386",
  },
}));

/**
 * Identité légale de facturation du vendeur.
 *
 * Toujours modifiable : ces informations figurent sur les factures à venir,
 * mais rien ne les verrouille. Le drapeau `complete` renvoyé par l'API sert
 * uniquement à avertir, jamais à empêcher la saisie.
 */
const SellerLegalInfo = ({
  legalInfo,
  formValues,
  onChange,
  onSubmit,
  isSaving,
  highlighted,
  missingFields = [],
  isConfirmOpen,
  onConfirm,
  onCancelConfirm,
  showTitle = true,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isMissing = (name) => missingFields.includes(name);

  return (
    <Box
      className={`${classes.container} ${highlighted ? classes.highlighted : ""}`}
    >
      {showTitle && (
        <Box className={classes.header}>
          <BusinessIcon style={{ fontSize: 22, color: "#3F51B5" }} />
          <Typography className={classes.title}>
            {t("seller_legal_info.title")}
          </Typography>
        </Box>
      )}

      {legalInfo?.loaded && !legalInfo?.complete && (
        <Box className={classes.incompleteBanner}>
          <WarningIcon style={{ fontSize: 20 }} />
          {t("seller_legal_info.incomplete_warning")}
        </Box>
      )}

      <Typography className={classes.description}>
        {t("seller_legal_info.description")}
      </Typography>

      <TextField
        name="companyName"
        label={t("seller_legal_info.company_name")}
        value={formValues?.companyName || ""}
        onChange={onChange}
        variant="outlined"
        size="small"
        required
        error={isMissing("companyName")}
        inputProps={{ maxLength: 120 }}
        className={classes.fieldFull}
      />

      <Box className={classes.fields}>
        <TextField
          name="companySiret"
          label={t("seller_legal_info.siret")}
          value={formValues?.companySiret || ""}
          onChange={onChange}
          variant="outlined"
          size="small"
          required
          error={isMissing("companySiret")}
          inputProps={{ maxLength: 14 }}
          className={classes.field}
        />
        <TextField
          name="vatNumber"
          label={t("seller_legal_info.vat_number")}
          value={formValues?.vatNumber || ""}
          onChange={onChange}
          variant="outlined"
          size="small"
          required
          error={isMissing("vatNumber")}
          inputProps={{ maxLength: 20 }}
          className={classes.field}
        />
      </Box>

      <TextField
        name="street"
        label={t("seller_legal_info.street")}
        value={formValues?.street || ""}
        onChange={onChange}
        variant="outlined"
        size="small"
        required
        error={isMissing("street")}
        inputProps={{ maxLength: 120 }}
        className={classes.fieldFull}
      />

      <Box className={classes.fields}>
        <TextField
          name="zipCode"
          label={t("seller_legal_info.zip_code")}
          value={formValues?.zipCode || ""}
          onChange={onChange}
          variant="outlined"
          size="small"
          required
          error={isMissing("zipCode")}
          inputProps={{ maxLength: 12 }}
          className={classes.fieldSmall}
        />
        <TextField
          name="city"
          label={t("seller_legal_info.city")}
          value={formValues?.city || ""}
          onChange={onChange}
          variant="outlined"
          size="small"
          required
          error={isMissing("city")}
          inputProps={{ maxLength: 80 }}
          className={classes.field}
        />
        <TextField
          name="country"
          label={t("seller_legal_info.country")}
          value={formValues?.country || ""}
          onChange={onChange}
          variant="outlined"
          size="small"
          required
          error={isMissing("country")}
          inputProps={{ maxLength: 60 }}
          className={classes.field}
        />
      </Box>

      <Button
        variant="contained"
        className={classes.saveButton}
        onClick={onSubmit}
        disabled={isSaving}
      >
        {isSaving ? (
          <CircularProgress size={20} style={{ color: "white" }} />
        ) : (
          t("seller_legal_info.save")
        )}
      </Button>

      <Dialog open={!!isConfirmOpen} onClose={onCancelConfirm}>
        <Box className={classes.confirmDialog}>
          <Typography className={classes.confirmTitle}>
            {t("seller_legal_info.confirm_title")}
          </Typography>
          <Typography className={classes.confirmText}>
            {t("seller_legal_info.confirm_text")}
          </Typography>
          <Box className={classes.confirmActions}>
            <Button className={classes.secondaryButton} onClick={onCancelConfirm}>
              {t("seller_legal_info.cancel")}
            </Button>
            <Button
              variant="contained"
              className={classes.saveButton}
              onClick={onConfirm}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                t("seller_legal_info.confirm_button")
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SellerLegalInfo;
